/**
 * SupportChatWidget — #3104 (#3096-D).
 *
 * Single-component support chat widget. Embedded in two modes (sub-card E
 * wires the actual mount points):
 *
 *   - `embed="fab"`     — marketing site floating-button + 380×560 panel.
 *   - `embed="drawer"`  — admin SPA right-side 420px drawer.
 *
 * Behaviours, all from `specs/3096-support-master-v0.1.md`:
 *   - Conversation cursor in localStorage with 14d TTL (§6.1).
 *   - WS to `wss://ws.2hives.ai/v1/support?conversation_id=<id>&token=<jwt>`,
 *     handling `support_message_appended`, `support_typing`,
 *     `support_sub_agent_state`, `support_kill_switch` (§4 WS frames).
 *   - HTTP fallback when WS unavailable / closed: 3s GET poll (§4).
 *   - Drag-drop file upload (uses the existing presigned-POST pattern via
 *     `supportChatApi.signUpload` → S3 PUT → `finalizeUpload`).
 *   - Markdown rendering for assistant messages (existing `Markdown`).
 *   - Typing indicator while the assistant is generating.
 *
 * Reserved-words hygiene (#3154): WS frame envelopes use `op` not `type`;
 * messages use `agent_role` not `role`. The widget never sends or reads a
 * top-level `type` / `role` / `model` / `version` / `data` field.
 *
 * Logged-in-only per founder decision #3096-1 (2026-06-03) — the widget
 * does NOT render the Turnstile gate; callers (sub-card E) are responsible
 * for only mounting this for authenticated users. The widget surfaces a
 * non-fatal "not signed in" hint if the underlying API rejects with 401 so
 * we degrade gracefully if the host page misconfigures auth.
 *
 * The widget keeps its own messages list rather than relying on a global
 * store; conversations are short-lived (max ~50 messages by §4 caps) and
 * a global store would force every page that imports this widget to pull
 * the store wiring.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Markdown } from "../Markdown";
import {
  supportChatApi,
  readSupportCursor,
  writeSupportCursor,
  clearSupportCursor,
  type SupportEmbedOrigin,
  type SupportMessage,
  type SupportAttachment,
} from "../../api/supportChatApi";
import { getFleetWsUrl } from "../../lib/fleetRoles";

// ── Public props ────────────────────────────────────────────────────────────

export type SupportEmbedMode = "fab" | "drawer";

export interface SupportChatWidgetProps {
  /** "fab" = floating bottom-right card; "drawer" = right-side admin slide-in. */
  embed:        SupportEmbedMode;
  /** Maps to `referrer_origin` on the conversation row (§3.1). */
  referrerOrigin: SupportEmbedOrigin;
  /** Caller-controlled open/close (sub-card E wires the trigger button). */
  open:         boolean;
  onClose:      () => void;
  /**
   * Cognito JWT — required for the WS hello. The widget will not open a WS
   * connection without it. Passing `null` puts the widget into polling-only
   * mode (still functional, just chattier).
   */
  jwt:          string | null;
  /**
   * Optional dependency injection — wired in unit tests to substitute the
   * native `WebSocket` constructor with a controllable mock.
   */
  wsFactory?:   (url: string) => WebSocketLike;
}

/** Subset of the WebSocket interface the widget relies on. Keeps tests sane. */
export interface WebSocketLike {
  readyState: number;
  onopen:    ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: string }) => void) | null;
  onclose:   ((ev: { code?: number }) => void) | null;
  onerror:   ((ev: unknown) => void) | null;
  send:  (payload: string) => void;
  close: () => void;
}

// ── WS frame shapes (envelope uses `op`, never `type`) ──────────────────────

interface SupportWsFrame {
  op: string;
  // Per-op payload follows; we read the fields we need by op.
  message?:        SupportMessage;
  conversation_id?: string;
  sub_agent_state?: string;
  reason?:          string;
}

// ── UX constants ────────────────────────────────────────────────────────────

const ANON_FILE_CAP_BYTES   = 20 * 1024 * 1024; // 20 MiB — spec §6.1, authed
const ANON_FILES_PER_MSG    = 5;
const POLL_INTERVAL_MS      = 3_000;             // §4 fallback cadence
const TYPING_GHOST_RESET_MS = 8_000;             // safety reset if no follow-up

const MIME_ALLOWLIST = new Set<string>([
  "image/png", "image/jpeg", "image/webp", "image/gif",
  "application/pdf", "text/plain", "text/markdown", "application/json",
  "text/csv",
  "text/x-python", "text/javascript", "text/typescript", "text/x-shellscript",
]);

function isAllowedMime(mime: string): boolean {
  // Some browsers report empty MIME for unknown text types — accept as text.
  return MIME_ALLOWLIST.has(mime) || mime === "" || mime.startsWith("text/");
}

// ── Pending upload (in-flight state) ────────────────────────────────────────

interface PendingUpload {
  upload_id:   string;
  filename:    string;
  mime:        string;
  size:        number;
  status:      "uploading" | "scanning" | "clean" | "infected" | "error";
  error?:      string;
}

// ── Component ──────────────────────────────────────────────────────────────

export function SupportChatWidget(props: SupportChatWidgetProps) {
  const { embed, referrerOrigin, open, onClose, jwt, wsFactory } = props;

  // ── State ────────────────────────────────────────────────────────────────
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages,       setMessages]       = useState<SupportMessage[]>([]);
  const [input,          setInput]          = useState("");
  const [sending,        setSending]        = useState(false);
  const [assistantTyping,setAssistantTyping]= useState(false);
  const [wsConnected,    setWsConnected]    = useState(false);
  const [killSwitch,     setKillSwitch]     = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [isDragOver,     setIsDragOver]     = useState(false);

  const wsRef        = useRef<WebSocketLike | null>(null);
  const pollRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef  = useRef<string | null>(null);

  // ── Conversation bootstrap on first open ────────────────────────────────
  // Guarded with a ref so the StrictMode double-invoke / re-render race doesn't
  // fire createConversation twice. `cancelled` from the effect cleanup is
  // unsafe here because setConversationId(cached.id) triggers a re-render that
  // re-runs the effect (which then short-circuits) and the original iife
  // would observe cancelled=true and skip the backfill setMessages call.
  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (!open || conversationId || bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    (async () => {
      const cached = readSupportCursor();
      if (cached) {
        setConversationId(cached.conversation_id);
        lastMessageIdRef.current = cached.last_message_id;
        // Backfill any messages we missed while the tab was closed.
        try {
          const page = await supportChatApi.pollMessages(cached.conversation_id, cached.last_message_id ?? undefined);
          if (page.messages.length > 0) {
            setMessages((prev) => mergeMessages(prev, page.messages));
            lastMessageIdRef.current = page.messages[page.messages.length - 1].message_id;
          }
        } catch (e) {
          setError(describeFetchError(e));
        }
        return;
      }
      try {
        const created = await supportChatApi.createConversation(referrerOrigin);
        setConversationId(created.conversation_id);
        writeSupportCursor({
          conversation_id: created.conversation_id,
          last_message_id: null,
          created_at:      Date.now(),
          updated_at:      Date.now(),
        });
      } catch (e) {
        setError(describeFetchError(e));
        bootstrappedRef.current = false;  // allow retry on next open cycle
      }
    })();
  }, [open, conversationId, referrerOrigin]);

  // Reset the bootstrap guard when the widget is closed so the next open
  // pass through the full create/restore flow.
  useEffect(() => {
    if (!open) bootstrappedRef.current = false;
  }, [open]);

  // ── WS connection lifecycle ─────────────────────────────────────────────
  useEffect(() => {
    if (!open || !conversationId || !jwt) {
      // No JWT → polling-only. We still want the poller below to run.
      return undefined;
    }
    const url = buildWsUrl(conversationId, jwt);
    const ws = (wsFactory ?? defaultWsFactory)(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      setError(null);
    };
    ws.onmessage = (ev) => {
      try {
        const frame = JSON.parse(ev.data) as SupportWsFrame;
        applyFrame(frame);
      } catch {
        // Drop malformed frames silently; never crash the widget.
      }
    };
    ws.onclose = () => {
      setWsConnected(false);
      setAssistantTyping(false);
    };
    ws.onerror = () => {
      setWsConnected(false);
    };

    return () => {
      try { ws.close(); } catch { /* ignore */ }
      if (wsRef.current === ws) wsRef.current = null;
    };
    // applyFrame is captured by closure with current setters; safe to depend
    // only on connection-shaping values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversationId, jwt, wsFactory]);

  // ── Polling fallback (only when not connected via WS) ───────────────────
  useEffect(() => {
    if (!open || !conversationId || wsConnected) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    const tick = async () => {
      try {
        const page = await supportChatApi.pollMessages(
          conversationId,
          lastMessageIdRef.current ?? undefined,
        );
        if (page.messages.length > 0) {
          setMessages((prev) => mergeMessages(prev, page.messages));
          lastMessageIdRef.current = page.messages[page.messages.length - 1].message_id;
          updateCursor(conversationId, lastMessageIdRef.current);
        }
      } catch {
        /* swallow — next tick retries; surface only on send. */
      }
    };
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [open, conversationId, wsConnected]);

  // ── Auto-scroll to bottom on new messages ───────────────────────────────
  useEffect(() => {
    if (!open) return;
    const el = messagesScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages.length, assistantTyping]);

  // ── Apply an incoming WS frame to local state ───────────────────────────
  const applyFrame = useCallback((frame: SupportWsFrame) => {
    switch (frame.op) {
      case "support_message_appended": {
        if (!frame.message) return;
        const m = frame.message;
        setMessages((prev) => mergeMessages(prev, [m]));
        lastMessageIdRef.current = m.message_id;
        setAssistantTyping(false);
        if (conversationId) updateCursor(conversationId, m.message_id);
        break;
      }
      case "support_typing": {
        setAssistantTyping(true);
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(
          () => setAssistantTyping(false),
          TYPING_GHOST_RESET_MS,
        );
        break;
      }
      case "support_sub_agent_state": {
        // Display-only — surfaced through the connection chip via wsConnected
        // already; explicit `reaped` flips the typing indicator off.
        if (frame.sub_agent_state === "reaped") setAssistantTyping(false);
        break;
      }
      case "support_kill_switch": {
        setKillSwitch(true);
        setAssistantTyping(false);
        try { wsRef.current?.close(); } catch { /* ignore */ }
        break;
      }
      default:
        // Unknown op — ignore (forward compatibility).
        break;
    }
  }, [conversationId]);

  // ── Send a message ──────────────────────────────────────────────────────
  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending || !conversationId || killSwitch) return;

    // Block send while any upload is still uploading/scanning.
    const blocking = pendingUploads.some((u) => u.status === "uploading" || u.status === "scanning");
    if (blocking) {
      setError("Wait for uploads to finish before sending.");
      return;
    }
    const cleanUploadIds = pendingUploads
      .filter((u) => u.status === "clean")
      .map((u) => u.upload_id);

    setError(null);
    setSending(true);

    // Optimistically append the user message so the UX feels instant.
    const optimistic: SupportMessage = {
      message_id:  `local-${Date.now()}`,
      agent_role:  "user",
      body_md:     text,
      created_at:  new Date().toISOString(),
      attachments: pendingUploads
        .filter((u) => u.status === "clean")
        .map<SupportAttachment>((u) => ({
          upload_id:   u.upload_id,
          s3_key:      "",
          mime:        u.mime,
          size:        u.size,
          scan_status: "clean",
        })),
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput("");
    setPendingUploads([]);

    try {
      await supportChatApi.sendMessage(conversationId, text, cleanUploadIds);
      setAssistantTyping(true);
    } catch (e) {
      setError(describeFetchError(e));
      // Roll back the optimistic user message so the user can retry.
      setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
      setInput(text);
    } finally {
      setSending(false);
    }
  }, [input, sending, conversationId, killSwitch, pendingUploads]);

  // ── File upload pipeline ────────────────────────────────────────────────
  const startUpload = useCallback(async (file: File) => {
    if (!conversationId) return;
    if (file.size > ANON_FILE_CAP_BYTES) {
      setError(`File ${file.name} exceeds the 20 MiB cap.`);
      return;
    }
    if (!isAllowedMime(file.type)) {
      setError(`File type "${file.type}" not allowed.`);
      return;
    }
    if (pendingUploads.length >= ANON_FILES_PER_MSG) {
      setError(`Max ${ANON_FILES_PER_MSG} attachments per message.`);
      return;
    }
    setError(null);

    let signed;
    try {
      signed = await supportChatApi.signUpload(conversationId, file.name, file.type, file.size);
    } catch (e) {
      setError(describeFetchError(e));
      return;
    }
    const pending: PendingUpload = {
      upload_id: signed.upload_id, filename: file.name, mime: file.type,
      size: file.size, status: "uploading",
    };
    setPendingUploads((prev) => [...prev, pending]);

    try {
      const form = new FormData();
      for (const [k, v] of Object.entries(signed.post_fields)) form.append(k, v);
      form.append("file", file);
      const putRes = await fetch(signed.post_url, { method: "POST", body: form });
      if (!putRes.ok) throw new Error(`upload failed (${putRes.status})`);
    } catch (e) {
      setPendingUploads((prev) =>
        prev.map((u) => u.upload_id === signed!.upload_id
          ? { ...u, status: "error", error: (e as Error).message }
          : u),
      );
      return;
    }

    setPendingUploads((prev) =>
      prev.map((u) => u.upload_id === signed!.upload_id ? { ...u, status: "scanning" } : u),
    );

    try {
      const fin = await supportChatApi.finalizeUpload(conversationId, signed.upload_id);
      // scan_status from the wire is `pending | clean | infected | error`; the
      // widget's PendingUpload status enum uses `scanning` while pending and
      // `clean | infected | error` once the scan finishes.
      const nextStatus: PendingUpload["status"] = fin.scan_status === "pending" ? "scanning" : fin.scan_status;
      setPendingUploads((prev) =>
        prev.map((u) => u.upload_id === signed!.upload_id
          ? { ...u, status: nextStatus }
          : u),
      );
      if (fin.scan_status === "infected") setError(`Upload ${file.name} was infected and rejected.`);
    } catch (e) {
      setPendingUploads((prev) =>
        prev.map((u) => u.upload_id === signed!.upload_id
          ? { ...u, status: "error", error: describeFetchError(e) }
          : u),
      );
    }
  }, [conversationId, pendingUploads.length]);

  // ── Drag-drop handlers ──────────────────────────────────────────────────
  const onDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    setIsDragOver(true);
  }, []);
  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);
  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);
  const onDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!e.dataTransfer) return;
    const files = Array.from(e.dataTransfer.files ?? []);
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      await startUpload(f);
    }
  }, [startUpload]);

  const onFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      await startUpload(f);
    }
    // Reset the input so picking the same file twice still fires onChange.
    e.target.value = "";
  }, [startUpload]);

  // ── End-of-conversation ─────────────────────────────────────────────────
  const close = useCallback(async () => {
    onClose();
    if (conversationId) {
      try { await supportChatApi.closeConversation(conversationId, "user_closed"); }
      catch { /* best-effort */ }
    }
    clearSupportCursor();
    setMessages([]);
    setConversationId(null);
    setPendingUploads([]);
    setInput("");
    lastMessageIdRef.current = null;
  }, [onClose, conversationId]);

  // ── Layout dims by embed mode ───────────────────────────────────────────
  const dims = useMemo(() => embed === "drawer"
    ? { width: 420, height: "100vh", right: 0, bottom: 0, borderRadius: 0 }
    : { width: 380, height: 560,     right: 16, bottom: 16, borderRadius: 8 },
    [embed],
  );

  if (!open) return null;

  return (
    <div
      data-testid={`support-chat-widget-${embed}`}
      role="dialog"
      aria-label="2hives support chat"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        position: "fixed",
        right: dims.right,
        bottom: dims.bottom,
        width: dims.width,
        maxWidth: "calc(100vw - 32px)",
        height: dims.height,
        maxHeight: embed === "drawer" ? "100vh" : "calc(100vh - 32px)",
        background: "var(--brand-bg-sunken)",
        border: "1px solid rgba(95,166,95,0.25)",
        borderRadius: dims.borderRadius,
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        display: "flex",
        flexDirection: "column",
        zIndex: 9000,
        fontFamily: "monospace",
        color: "var(--brand-text-subtle)",
      }}
    >
      {/* Header */}
      <div style={{
        padding: "10px 12px",
        borderBottom: "1px solid rgba(95,166,95,0.2)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <strong style={{ fontSize: "0.85rem", color: "var(--brand-text-link)" }}>2hives support</strong>
          <span data-testid="support-chat-connection" style={{ fontSize: "0.65rem", color: wsConnected ? "var(--brand-text-link)" : "var(--brand-text-muted)" }}>
            {wsConnected ? "connected" : "polling"}
          </span>
        </div>
        <button
          onClick={close}
          aria-label="Close support chat"
          style={{
            background: "none",
            border: "1px solid rgba(95,166,95,0.25)",
            color: "var(--brand-text-link)",
            borderRadius: 4,
            padding: "2px 8px",
            cursor: "pointer",
            fontFamily: "monospace",
            fontSize: "0.7rem",
          }}
        >
          ✕
        </button>
      </div>

      {/* Kill-switch banner */}
      {killSwitch && (
        <div
          data-testid="support-chat-killswitch"
          role="alert"
          style={{
            padding: "8px 12px",
            background: "rgba(168, 60, 60, 0.12)",
            color: "var(--brand-status-error-text)",
            borderBottom: "1px solid rgba(168, 60, 60, 0.3)",
            fontSize: "0.7rem",
          }}
        >
          Support is temporarily offline. Please email founder@2hives.ai for urgent issues.
        </div>
      )}

      {/* Messages */}
      <div
        ref={messagesScrollRef}
        data-testid="support-chat-messages"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "10px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          fontSize: "0.75rem",
          lineHeight: 1.5,
          position: "relative",
        }}
      >
        {messages.length === 0 && (
          <div style={{ color: "var(--brand-text-muted)", fontStyle: "italic" }}>
            Hi! I'm 2hives support. Tell me what's broken or what you'd like
            help with — you can also drag-drop files (screenshots, logs, etc.).
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.message_id} message={m} />
        ))}
        {assistantTyping && (
          <div
            data-testid="support-chat-typing"
            aria-live="polite"
            style={{
              alignSelf: "flex-start",
              color: "var(--brand-text-muted)",
              fontStyle: "italic",
              fontSize: "0.7rem",
            }}
          >
            support is typing…
          </div>
        )}
        {error && (
          <div
            data-testid="support-chat-error"
            role="alert"
            style={{
              color: "var(--brand-status-error-text)",
              background: "rgba(168, 60, 60, 0.12)",
              border: "1px solid rgba(168, 60, 60, 0.3)",
              borderRadius: 6,
              padding: "6px 8px",
            }}
          >
            {error}
          </div>
        )}

        {isDragOver && (
          <div
            data-testid="support-chat-dropzone"
            aria-label="Drop files to attach"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(10,26,14,0.85)",
              border: "2px dashed rgba(95,166,95,0.6)",
              color: "var(--brand-text-link)",
              fontWeight: 700,
              pointerEvents: "none",
            }}
          >
            Drop files to attach
          </div>
        )}
      </div>

      {/* Pending-uploads tray */}
      {pendingUploads.length > 0 && (
        <div
          data-testid="support-chat-uploads"
          style={{
            padding: "4px 12px",
            borderTop: "1px solid rgba(95,166,95,0.15)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            fontSize: "0.65rem",
            color: "var(--brand-text-muted)",
            maxHeight: 80,
            overflowY: "auto",
          }}
        >
          {pendingUploads.map((u) => (
            <div key={u.upload_id} data-testid={`support-upload-${u.upload_id}`}>
              {u.filename} · {u.status}{u.error ? ` · ${u.error}` : ""}
            </div>
          ))}
        </div>
      )}

      {/* Composer */}
      <div style={{
        borderTop: "1px solid rgba(95,166,95,0.2)",
        padding: "8px 10px",
        display: "flex",
        gap: 6,
        alignItems: "flex-end",
      }}>
        <label
          aria-label="Attach a file"
          style={{
            border: "1px solid rgba(95,166,95,0.25)",
            borderRadius: 4,
            padding: "4px 8px",
            cursor: "pointer",
            fontSize: "0.75rem",
            color: "var(--brand-text-link)",
            background: "var(--brand-bg-deep)",
          }}
        >
          📎
          <input
            data-testid="support-chat-file-input"
            type="file"
            multiple
            onChange={onFileInputChange}
            style={{ display: "none" }}
          />
        </label>
        <textarea
          data-testid="support-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder={killSwitch ? "support offline" : "Ask support — Enter to send, Shift+Enter for newline"}
          rows={2}
          disabled={sending || killSwitch || !conversationId}
          style={{
            flex: 1,
            background: "var(--brand-bg-deep)",
            color: "var(--brand-text-subtle)",
            border: "1px solid rgba(95,166,95,0.25)",
            borderRadius: 4,
            padding: "6px 8px",
            fontFamily: "monospace",
            fontSize: "0.75rem",
            resize: "none",
            outline: "none",
          }}
        />
        <button
          data-testid="support-chat-send"
          onClick={() => void send()}
          disabled={sending || killSwitch || !input.trim() || !conversationId}
          style={{
            background: sending || killSwitch || !input.trim() ? "rgba(95,166,95,0.08)" : "rgba(95,166,95,0.22)",
            color: sending || killSwitch || !input.trim() ? "var(--brand-text-disabled)" : "var(--brand-text-link)",
            border: "1px solid rgba(95,166,95,0.3)",
            borderRadius: 4,
            padding: "4px 10px",
            cursor: sending || killSwitch || !input.trim() ? "not-allowed" : "pointer",
            fontFamily: "monospace",
            fontSize: "0.75rem",
          }}
        >
          {sending ? "…" : "send"}
        </button>
      </div>
    </div>
  );
}

// ── Message bubble ──────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: SupportMessage }) {
  const isUser   = message.agent_role === "user";
  const isSystem = message.agent_role === "system";
  return (
    <div
      data-testid={`support-msg-${message.agent_role}`}
      style={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "85%",
        background: isUser
          ? "rgba(95,166,95,0.18)"
          : isSystem ? "rgba(95,166,95,0.04)" : "rgba(95,166,95,0.07)",
        border: `1px solid rgba(95,166,95,${isUser ? 0.35 : 0.15})`,
        borderRadius: 6,
        padding: "6px 8px",
        wordBreak: "break-word",
      }}
    >
      {isUser
        ? <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "0.75rem" }}>{message.body_md}</pre>
        : <Markdown compact>{message.body_md}</Markdown>
      }
      {message.attachments && message.attachments.length > 0 && (
        <div style={{
          marginTop: 4,
          fontSize: "0.62rem",
          color: "var(--brand-text-muted)",
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
        }}>
          {message.attachments.map((a) => (
            <span key={a.upload_id} data-testid={`support-attach-${a.upload_id}`}>
              📎 {a.mime} · {formatBytes(a.size)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function mergeMessages(existing: SupportMessage[], incoming: SupportMessage[]): SupportMessage[] {
  if (incoming.length === 0) return existing;
  const byId = new Map<string, SupportMessage>();
  for (const m of existing) byId.set(m.message_id, m);
  for (const m of incoming) byId.set(m.message_id, m);
  // Drop optimistic placeholders (local-*) once the real server message arrives.
  const cleaned: SupportMessage[] = [];
  for (const m of byId.values()) {
    if (m.message_id.startsWith("local-")) {
      // Look for a server message with the same body authored by the user.
      const dup = Array.from(byId.values()).some(
        (x) => x !== m && x.agent_role === m.agent_role && x.body_md === m.body_md && !x.message_id.startsWith("local-"),
      );
      if (dup) continue;
    }
    cleaned.push(m);
  }
  cleaned.sort((a, b) => a.message_id < b.message_id ? -1 : a.message_id > b.message_id ? 1 : 0);
  return cleaned;
}

function buildWsUrl(conversationId: string, jwt: string): string {
  const base = getFleetWsUrl();
  // Strip any path/query from the base, then append /v1/support per spec §4.
  const baseHost = base.replace(/\/+$/, "");
  const sep = "?";
  return `${baseHost}/v1/support${sep}conversation_id=${encodeURIComponent(conversationId)}&token=${encodeURIComponent(jwt)}`;
}

function defaultWsFactory(url: string): WebSocketLike {
  // The DOM `WebSocket` typing types the handlers as `(this: WebSocket, ev: Event) => any`;
  // the structural `WebSocketLike` interface uses `(ev: unknown) => void` so the
  // widget can substitute a controllable mock in tests without dragging the
  // full DOM Event surface in. Cast once at the boundary.
  return new WebSocket(url) as unknown as WebSocketLike;
}

function describeFetchError(e: unknown): string {
  if (e instanceof Error) return e.message || "network error";
  return "network error";
}

function updateCursor(conversationId: string, lastMessageId: string | null): void {
  writeSupportCursor({
    conversation_id: conversationId,
    last_message_id: lastMessageId,
    created_at:      Date.now(), // first-time create_at is overwritten on reload from cache; fine
    updated_at:      Date.now(),
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
